import { ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { Tooltip, Whisper } from "rsuite";
import { callFunctionIfExists } from "../../utils/GeneralUtils";

export type MdiIconType = `mdi mdi-${string}`;

interface BaseProps {
  name: string;
  onClick: undefined | ((e?: any) => any);
  disabled?: boolean;
  active?: boolean;
  actionIcon?: MdiIconType;
  isDefault?: boolean;
  actionColor?: string;
  onActionClick?: () => void;
  tooltip?: string;
  optionColor?: string;
  showChevron?: boolean;
}

interface PropsWithIcon extends BaseProps {
  icon: MdiIconType;
  customIcon?: never;
}

interface PropsWithCustomIcon extends BaseProps {
  customIcon: ReactNode;
  icon?: never;
}

type Props = PropsWithIcon | PropsWithCustomIcon;

const DropdownOptionWithIcon = ({
  name,
  icon,
  customIcon,
  onClick,
  disabled,
  active,
  actionIcon,
  actionColor,
  onActionClick,
  tooltip,
  optionColor,
  showChevron = true,
  isDefault = false,
}: Props) => {
  const handleClick = (e: any) => {
    callFunctionIfExists(onClick, e);
  };

  const nameRef = useRef<HTMLDivElement>(null);
  const [nameWidth, setNameWidth] = useState(0);

  const textWidth = (text: string, fontProp = "13px Poppins") => {
    let tag = document.createElement("div");
    tag.style.position = "absolute";
    tag.style.left = "-99in";
    tag.style.whiteSpace = "nowrap";
    tag.style.font = fontProp;
    tag.style.fontWeight = "500";
    tag.innerHTML = text;

    document.body.appendChild(tag);
    var result = tag.clientWidth;
    document.body.removeChild(tag);
    return result;
  };

  useEffect(() => {
    setNameWidth(nameRef?.current?.clientWidth ?? 0);
  }, [nameRef?.current]);

  return (
    <Whisper
      placement="auto"
      trigger={tooltip ? "hover" : "none"}
      speaker={<Tooltip>{tooltip}</Tooltip>}
    >
      <div>
        <li
          className={`rs-dropdown-item no-ripple ${
            disabled ? "disabled" : ""
          } ${active ? "bg-dropdown-selected" : ""}`}
          onClick={disabled ? undefined : (e) => handleClick(e)}
        >
          <div
            className="d-flex align-items-center w-100"
            style={{
              position: "relative",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            {customIcon && customIcon}
            {!customIcon && (
              <i
                style={{
                  fontSize: 18,
                  color: !disabled && optionColor ? optionColor : "unset",
                }}
                className={`${icon} pr-2 text-${
                  disabled ? "light" : actionColor ? actionColor : "dark"
                }`}
              ></i>
            )}
            <div className="d-inline-block" style={{ width: "100%" }}>
              <div
                className={`d-inline-block ${
                  disabled ? "text-light" : "text-dark"
                }`}
                style={{
                  position: "relative",
                  top: "2px",
                  width: "calc(100% - 40px)",
                  overflowX: "hidden",
                  overflowY: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                ref={nameRef}
              >
                {textWidth(name) >= nameWidth ? (
                  <>
                    <Whisper
                      trigger="hover"
                      placement="auto"
                      speaker={<Tooltip>{name}</Tooltip>}
                    >
                      <span
                        style={{
                          color:
                            !disabled && optionColor ? optionColor : "unset",
                        }}
                      >
                        {name}
                      </span>
                    </Whisper>
                  </>
                ) : (
                  <span
                    style={{
                      color: !disabled && optionColor ? optionColor : "unset",
                      position: "relative",
                      top: 1,
                    }}
                  >
                    {isDefault && (
                      <i
                        className="mdi mdi-star ml-1 mr-0 text-warning"
                        style={{
                          fontSize: 16,
                          position: "relative",
                          top: 1,
                          left: -6,
                        }}
                      />
                    )}
                    {name}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                outline
                color={actionColor || "secondary"}
                disabled={disabled ? true : false}
                style={{
                  height: "1.3rem",
                  width: "1.3rem",
                  position: "absolute",
                  right: -1,
                  top: 2,
                }}
                className="btn-icon-only"
                onClick={
                  onActionClick
                    ? (e) => {
                        e.stopPropagation();
                        onActionClick();
                      }
                    : undefined
                }
              >
                {showChevron && (
                  <i
                    className={actionIcon || "mdi mdi-chevron-right"}
                    style={{
                      fontSize: "20px",
                      lineHeight: 1,
                      position: "relative",
                      top: 1,
                    }}
                  />
                )}
              </Button>
            </div>
          </div>
        </li>
      </div>
    </Whisper>
  );
};

export default DropdownOptionWithIcon;
